import React, { useState, useEffect, useMemo } from 'react';
import { budgetSetupService } from '../../services/budgetSetupService';
import MantleGrid from '../../components/MantleGrid';
import { cell, GRID } from '../../styles/gridStyles';

const VersionSetupModal = ({ onClose }) => {
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'add'
  const [loadError, setLoadError] = useState(null);
  
  // New Version Form State
  const [formData, setFormData] = useState({
    version_id: '',
    version_code: '',
    description: '',
    financial_year: new Date().getFullYear() + 1
  });
  const [formError, setFormError] = useState(null);

  const loadVersions = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await budgetSetupService.getVersions();
      if (res.success) {
        setVersions(res.data);
      } else {
        // A non-success body (e.g. "Missing x-db-name header") used to be
        // swallowed here, leaving an empty grid that looked like "no versions".
        setVersions([]);
        setLoadError(res.message || 'The server rejected the request for budget versions.');
      }
    } catch (error) {
      console.error('Failed to load versions', error);
      setVersions([]);
      setLoadError(
        error.response?.data?.message
        || error.response?.data?.err
        || error.message
        || 'Could not reach the server.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVersions();
  }, []);

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await budgetSetupService.updateVersion(id, { is_active: !currentStatus });
      loadVersions();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this version?')) return;
    try {
      await budgetSetupService.deleteVersion(id);
      loadVersions();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete version');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      await budgetSetupService.createVersion(formData);
      setFormData({ version_id: '', version_code: '', description: '', financial_year: new Date().getFullYear() + 1 });
      setViewMode('list');
      loadVersions();
    } catch (error) {
      setFormError(error.response?.data?.message || 'Failed to create version');
    }
  };

  const filteredVersions = showInactive ? versions : versions.filter(v => v.is_active);

  // ---------------------------------------------------------------------------
  // MantleGrid Configuration for the List View
  // ---------------------------------------------------------------------------
  const columns = useMemo(() => [
    { columnId: 'code', width: 160, resizable: true },
    { columnId: 'description', width: 320, resizable: true },
    { columnId: 'fy', width: 80, resizable: true },
    { columnId: 'status', width: 100, resizable: true },
    { columnId: 'action_toggle', width: 120, resizable: false },
    { columnId: 'action_delete', width: 100, resizable: false },
  ], []);

  const gridRows = useMemo(() => {
    const headerRow = {
      rowId: 'header',
      height: GRID.size.headerRow,
      cells: [
        cell.header('Code'),
        cell.header('Description'),
        cell.header('FY'),
        cell.header('Status'),
        cell.header(''), // Empty header for actions
        cell.header(''), 
      ],
    };

    if (filteredVersions.length === 0) {
      return [headerRow]; // Grid handles empty states cleanly, but needs at least a header
    }

    const dataRows = filteredVersions.map((v, idx) => ({
      rowId: v.version_id,
      height: GRID.size.dataRow,
      cells: [
        cell.label(v.version_code, { idx }),
        cell.text(v.description, { idx }),
        cell.text(v.financial_year.toString(), { idx }),
        // Status indicator pill
        cell.tag(v.is_active ? 'Active' : 'Inactive', {
          active: v.is_active,
          activeColor: '#15803d', activeBg: '#dcfce7', // Green
          mutedColor: '#475569', mutedBg: '#f1f5f9'  // Slate
        }),
        // Clickable Toggle Action
        cell.tag(v.is_active ? 'Deactivate' : 'Activate', {
          active: true,
          activeColor: v.is_active ? '#b45309' : '#15803d', 
          activeBg: v.is_active ? '#fef3c7' : '#dcfce7',
          clickable: true
        }),
        // Clickable Delete Action
        cell.tag('Delete', {
          active: true,
          activeColor: '#b91c1c', activeBg: '#fee2e2', // Red
          clickable: true
        })
      ],
    }));

    return [headerRow, ...dataRows];
  }, [filteredVersions]);

  // Intercept clicks on the action columns to trigger our React functions
  // instead of letting the grid select the cell for editing.
  const handleFocusLocationChanging = (location) => {
    if (location.rowId === 'header') return true;

    const versionId = String(location.rowId);
    const colId = String(location.columnId);

    if (colId === 'action_toggle') {
      const v = versions.find(ver => ver.version_id === versionId);
      if (v) handleToggleActive(versionId, v.is_active);
      return false; // Prevent cell focus
    }

    if (colId === 'action_delete') {
      handleDelete(versionId);
      return false; // Prevent cell focus
    }

    return true; // Allow normal focus for other cells
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans">
      <div className="bg-white dark:bg-slate-800 w-full max-w-[880px] rounded-xl shadow-2xl flex flex-col h-[80vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Manage Budget Versions</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            ✕
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setViewMode(viewMode === 'list' ? 'add' : 'list')}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 transition-colors"
            >
              {viewMode === 'list' ? '+ New Version' : '← Back to List'}
            </button>
          </div>
          
          {viewMode === 'list' && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Show Inactive</span>
              <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                <input 
                  type="checkbox" 
                  className="toggle-checkbox absolute block w-5 h-5 rounded-full bg-white border-4 appearance-none cursor-pointer transition-transform duration-200 ease-in-out"
                  style={{ transform: showInactive ? 'translateX(100%)' : 'translateX(0)', borderColor: showInactive ? '#0f172a' : '#cbd5e1' }}
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <div className={`toggle-label block overflow-hidden h-5 rounded-full cursor-pointer ${showInactive ? 'bg-slate-900' : 'bg-slate-300'}`}></div>
              </div>
            </label>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden relative">
          {viewMode === 'add' ? (
            <div className="max-w-2xl mx-auto p-6 overflow-y-auto h-full">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">Create New Version</h3>
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                  {formError}
                </div>
              )}
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Version ID (Internal Key)</label>
                    <input 
                      required type="text" maxLength={50} placeholder="e.g. V3"
                      value={formData.version_id} onChange={e => setFormData({...formData, version_id: e.target.value})}
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-md outline-none focus:ring-2 focus:ring-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Version Code</label>
                    <input 
                      required type="text" maxLength={50} placeholder="e.g. FY27_DRAFT"
                      value={formData.version_code} onChange={e => setFormData({...formData, version_code: e.target.value})}
                      className="w-full p-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-md outline-none focus:ring-2 focus:ring-slate-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <input 
                    required type="text" maxLength={255} placeholder="e.g. FY 2027 Initial Draft Budget"
                    value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full p-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-md outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Financial Year</label>
                  <input 
                    required type="number"
                    value={formData.financial_year} onChange={e => setFormData({...formData, financial_year: parseInt(e.target.value, 10)})}
                    className="w-1/3 p-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-md outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setViewMode('list')} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 text-sm text-white bg-slate-900 rounded-md hover:bg-slate-800">Save Version</button>
                </div>
              </form>
            </div>
          ) : loadError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm font-semibold text-red-700">Couldn’t load budget versions</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md break-words">{loadError}</p>
              <button
                onClick={loadVersions}
                className="mt-1 px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-md hover:bg-slate-800 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="absolute inset-0">
               {/* MantleGrid fills the content area; its internal overflow
                   scrolls the rows when the list grows past the modal height. */}
               <MantleGrid
                  rows={gridRows}
                  columns={columns}
                  isLoading={isLoading}
                  onFocusLocationChanging={handleFocusLocationChanging}
                  stickyBottomRows={0}
                  enableRangeSelection={false}
                  enableFillHandle={false}
                  enableRowSelection={false}
               />

               {/* An empty grid is ambiguous — say which kind of empty it is. */}
               {!isLoading && filteredVersions.length === 0 && (
                 <div className="absolute inset-x-0 top-14 flex justify-center px-6 pointer-events-none">
                   <p className="text-xs text-slate-500 dark:text-slate-400 text-center max-w-sm">
                     {versions.length === 0
                       ? 'No budget versions exist yet — use “+ New Version” to create one.'
                       : 'All versions are inactive — turn on “Show Inactive” to see them.'}
                   </p>
                 </div>
               )}
            </div>
          )}
        </div>
      </div>
    </div>
  ); 
}; 

export default VersionSetupModal;